// flow/hooks/useFlowFileHandlers.ts
import { useCallback } from 'react'
import { toast } from 'sonner'
import type { Node } from '@xyflow/react'
import { useFileStore } from '@/stores/fileStore'

interface UseFlowFileHandlersProps {
    projectId: string
    user: any
    screenToFlowPosition: (position: { x: number; y: number }) => { x: number; y: number }
    addNode: (node: Node) => void
    createNodeResource: (params: any) => void
}

export function useFlowFileHandlers({
    projectId,
    user,
    screenToFlowPosition,
    addNode,
    createNodeResource,
}: UseFlowFileHandlersProps) {
    const { uploadStandaloneFile } = useFileStore()

    const handleFileDrop = useCallback(
        async (event: React.DragEvent, dropPosition?: { x: number; y: number }) => {
            event.preventDefault()
            const files = event.dataTransfer?.files
            if (!files?.length) return

            // Use provided dropPosition or default to center
            const position = dropPosition
                ? screenToFlowPosition(dropPosition)
                : screenToFlowPosition({
                    x: window.innerWidth / 2,
                    y: window.innerHeight / 2,
                })

            for (const file of files) {
                const formData = new FormData()
                formData.append('file', file)
                formData.append('projectId', projectId)
                formData.append('userEmail', user.email)
                formData.append('userId', user.id)

                try {
                    const uploadedFile = await uploadStandaloneFile(formData)
                    if (uploadedFile) {
                        // Check if the file is a PDF
                        const isPDF = uploadedFile.contentType === 'application/pdf' ||
                            file.name.toLowerCase().endsWith('.pdf')

                        if (isPDF) {
                            // Create PDF resource node
                            const newNode: Node = {
                                id: `pdf-resource-node-${Date.now()}`,
                                type: 'noderesourcepdf',
                                position,
                                width: 464,
                                height: 692,
                                data: {
                                    label: 'PDF Resource',
                                    informationClassification: 'Data',
                                    filename: uploadedFile.filename,
                                    createdBy: user.email,
                                    createdAt: uploadedFile.createdAt,
                                    url: uploadedFile.url,
                                    fileId: uploadedFile.id,
                                    contentType: uploadedFile.contentType,
                                    size: uploadedFile.size,
                                    groupId: uploadedFile.groupId,
                                },
                            }
                            addNode(newNode)
                            toast.success(`PDF uploaded: ${uploadedFile.filename}`)
                        } else {
                            // Create regular resource node
                            createNodeResource({
                                fileId: uploadedFile.id,
                                filename: uploadedFile.filename,
                                url: uploadedFile.url,
                                contentType: uploadedFile.contentType,
                                size: uploadedFile.size,
                                createdAt: uploadedFile.createdAt,
                                groupId: uploadedFile.groupId,
                                position,
                            })
                            toast.success(`File uploaded: ${uploadedFile.filename}`)
                        }
                    } else {
                        toast.error(`Failed to upload: ${file.name}`)
                        console.error('File upload failed:', file.name, uploadedFile)
                    }
                } catch (error) {
                    toast.error(`Upload error: ${file.name}`)
                    console.error('File upload error:', error)
                }
            }
        },
        [projectId, user, screenToFlowPosition, addNode, createNodeResource, uploadStandaloneFile]
    )
    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault()
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy'
        }
    }, [])

    return {
        handleFileDrop,
        onDragOver,
    }
}
